/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useState } from 'react';
import { object, string } from 'prop-types';
import { makeStyles, TextField } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import validateEmailAddress from '../../../../../../helpers/validateEmailAddress';
import { emitCustomerIdentificationEvent } from '../../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { setOfferInboxData } from '../../../../../../../state/ducks/App/App-Actions';

const useInputStyles = makeStyles((theme) => ({
    input: (styles) => ({
        margin: styles?.margin || '0 0 10px',
    }),
    emailField: {
        width: '100%',
    },
    phoneNumberField: {
        width: '100%',
    },
    form: {
        margin: '8px 0',
    },
}));

const InputField = ({
    data, offer, formId,
}) => {
    const classes = useInputStyles({
        margin: data?.margin,
    });

    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [displayPhoneNumber, setDisplayPhoneNumber] = useState('');

    const campaignPayload = offer?.campaign?.campaignResponses?.[0]?.payload || {};

    const formatPhoneNumber = (phoneNo) => {
        let updatedPhone = phoneNo;

        if (updatedPhone.length > 10) {
            // International numbers
            updatedPhone = updatedPhone.replace(/^(\d\d)(\d{3})(\d{3})(\d{0,4}).*/, '$1 $2 $3 $4');
        } else if (updatedPhone.length > 6) {
            // big to 5 digit do the form is it is not already there 201123435 -> (201) 123-435
            updatedPhone = updatedPhone.replace(/^(\d\d\d)(\d{3})(\d{0,4}).*/, '($1) $2-$3');
        } else if (updatedPhone.length > 3) {
            // bit to 3 digit do the form is it is not already there 20123 -> (201) 23
            updatedPhone = updatedPhone.replace(/^(\d\d\d)(\d{0,4})/, '($1) $2');
        } else {
            // bit to 1 digit do the form is it is not already there 201 -> (201)
            updatedPhone = updatedPhone.replace(/^(\d*)/, '$1');
        }

        return updatedPhone;
    };

    const derivePhoneNumber = (phone, event = null) => {
        const V1 = /^\((\d\d\d)\)\s$/; // the phone come with (201), dont need continue the process
        const V2 = /^\((\d\d\d)\)\s(\d\d\d)-$/; // the phone come with (201) 235-, dont need continue the process
        const target = event?.target;
        // start transform the code
        if (V1.test(phone)) {
            return String(phone).replace(V1, '$1');
        }
        if (V2.test(phone)) {
            String(phone).replace(V2, '($1) $2');
        }

        let updatedPhone = String(phone).replace(/[^0-9]+/g, '');
        updatedPhone = formatPhoneNumber(updatedPhone, target);
        return updatedPhone;
    };

    const validatePhoneNumber = (phone) => {
        const regex = new RegExp(/^[0-9]+$|^$/);
        const isValid = regex.test(phone);
        if (isValid && phoneNumber.length >= 10) {
            setError('');
            return true;
        }
        setError('Please enter a valid phone number.');
        return false;
    };

    const handleChange = (e) => {
        const inputValue = e.target?.value;
        if (data?.type === 'phone') {
            const displayPhone = derivePhoneNumber(inputValue, e);
            setDisplayPhoneNumber(displayPhone);

            const phone = inputValue.replace(/\D+/g, '');
            setPhoneNumber(phone);
        } else {
            setEmail(inputValue.trim());
        }
    };

    const validateEmail = () => {
        const isValid = validateEmailAddress(email);
        if (isValid) {
            setError('');
            return true;
        }
        setError('Please enter a valid email address.');
        return false;
    };

    const handleSubscription = (isSuccessfull) => {
        const user = {};
        if (email) {
            user.email = email;
        }

        if (phoneNumber) {
            user.phone = phoneNumber;
        }

        if (isSuccessfull) {
            dispatch(emitCustomerIdentificationEvent({
                customEvent: {
                    eventDefinitionKey: campaignPayload?.eventDefinitionKey,
                    offerName: campaignPayload?.offerName,
                    offerDesc: campaignPayload?.offerDesc,
                    offerPercent: campaignPayload?.offerPercent,
                },
                page: 'home',
                salesforceResponse: offer,
                eventType: 'clickstream.customer-identification',
                eventSubType: 'journey-email-trigger',
                user,
            }));

            if (data?.type === 'email') {
                dispatch(setOfferInboxData({
                    offerGatewayOptin: true,
                    email,
                }));
            } else {
                dispatch(setOfferInboxData({
                    offerGatewayOptinSMS: true,
                    phone: phoneNumber,
                    phoneNumberSuccess: true,
                }));
            }
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (data?.type === 'phone' && !validatePhoneNumber(phoneNumber)) {
            return;
        }

        if (data?.type === 'email' && !validateEmail()) {
            return;
        }

        handleSubscription(true);
    };

    switch (data?.type) {
        case 'email':

            return (
                <form onSubmit={handleSubmit} id={formId} className={classes.form}>
                    <div className={classes.input}>
                        <TextField
                            label={data?.label || 'Email'}
                            variant={data?.variant || 'outlined'}
                            type={data?.type || 'text'}
                            className={classes.emailField}
                            value={email}
                            id={data?.label || 'email'}
                            onChange={handleChange}
                            onBlur={validateEmail}
                            onFocus={() => setError('')}
                            error={!!error}
                            helperText={error}
                        />
                    </div>
                </form>
            );

        case 'phone':
            return (
                <form onSubmit={handleSubmit} id={formId} className={classes.form}>
                    <div className={classes.input}>
                        <TextField
                            label={data?.label}
                            variant={data?.variant || 'outlined'}
                            type="tel"
                            className={classes.phoneNumberField}
                            value={displayPhoneNumber}
                            id={data?.label}
                            onChange={handleChange}
                            onBlur={() => validatePhoneNumber(phoneNumber)}
                            onFocus={() => setError('')}
                            error={!!error}
                            helperText={error}
                        />
                    </div>
                </form>
            );

        default:
            return null;
    }
};

InputField.propTypes = {
    data: object,
    offer: object,
    formId: string,
};
InputField.defaultProps = {
    data: {},
    offer: {},
    formId: '',
};

export default InputField;
